import { store } from '@/store/store.js';

/**
 * v-perm directive
 * Disables an element and adds the 'disabled' styling if the user lacks the specified permission.
 * Also adds an element title (like a tooltip) showing the required permission.
 * 
 * Usage: v-perm="'PERMISSION.NAME'"
 * Example: <button v-perm="'FTL.MODIFY'">Edit</button>
 */

function formatPermission(permission) {
  return permission.replace('.', ' ');
}

function preventInteraction(e) {
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();
  return false;
}

export default {
  mounted(el, binding) {
    const permission = binding.value;
    const hasPerm = store.getters['login/userHasPerm'](permission);
    
    if (!hasPerm) {
      // Disable the element
      el.disabled = true;
      
      // For Vue components like Checkbox, find and disable internal inputs
      const inputs = el.querySelectorAll('input, textarea, select, button');
      inputs.forEach(input => {
        input.disabled = true;
      });
      
      // Add disabled class
      if (!el.classList.contains('disabled')) {
        el.classList.add('disabled');
      }
      
      // Override pointer-events to allow tooltip and add cursor
      el.style.pointerEvents = 'auto';
      el.style.cursor = 'default';
      
      // Add title attribute for basic tooltip with formatted permission
      el.setAttribute('title', `Required permission: ${formatPermission(permission)}`);
      
      // Prevent all interaction
      el.addEventListener('click', preventInteraction, true);
      el.addEventListener('mousedown', preventInteraction, true);
      el.addEventListener('mouseup', preventInteraction, true);
      el.addEventListener('dblclick', preventInteraction, true);
      
      // Store permission for later reference
      el._requiredPermission = permission;
      el._preventInteraction = preventInteraction;
    }
  },
  
  updated(el, binding) {
    const permission = binding.value;
    const hasPerm = store.getters['login/userHasPerm'](permission);
    
    if (!hasPerm) {
      // Disable the element
      el.disabled = true;
      
      // For Vue components like Checkbox, find and disable internal inputs
      const inputs = el.querySelectorAll('input, textarea, select, button');
      inputs.forEach(input => {
        input.disabled = true;
      });
      
      // Add disabled class
      if (!el.classList.contains('disabled')) {
        el.classList.add('disabled');
      }
      
      // Override pointer-events to allow tooltip and add cursor
      el.style.pointerEvents = 'auto';
      el.style.cursor = 'default';
      
      // Add title attribute for basic tooltip with formatted permission
      if (!el.getAttribute('title')) {
        el.setAttribute('title', `Required permission: ${formatPermission(permission)}`);
      }
      
      // Add event listeners if not already added
      if (!el._preventInteraction) {
        el.addEventListener('click', preventInteraction, true);
        el.addEventListener('mousedown', preventInteraction, true);
        el.addEventListener('mouseup', preventInteraction, true);
        el.addEventListener('dblclick', preventInteraction, true);
        el._preventInteraction = preventInteraction;
      }
      
      el._requiredPermission = permission;
    }
  },
  
  unmounted(el) {
    // Clean up event listeners
    if (el._preventInteraction) {
      el.removeEventListener('click', el._preventInteraction, true);
      el.removeEventListener('mousedown', el._preventInteraction, true);
      el.removeEventListener('mouseup', el._preventInteraction, true);
      el.removeEventListener('dblclick', el._preventInteraction, true);
      delete el._preventInteraction;
    }
    delete el._requiredPermission;
  }
};
